import React, {useRef} from "react";
import GenericLayout from "layouts/generic";
import css from "./index.module.scss";
import Card from "@/components/card";
import Button from "@/components/button";
import ContactSection from "sections/contact";
import Picture from "@/components/picture";
import DlLogoSvg from "/public/images/logos/deviatelabs/sealText.svg";
import DeviateLabsSealSvg from "/public/images/logos/deviatelabs/seal.svg";
import { useEffect, useState } from "react";
import horizontalLoop from "scripts/gsap/horizontalLoop";
import Link from "next/link";
import CircleCheckmarkSvg from "/public/images/icons/circleCheckmark.svg";
import StarSvg from "/public/images/icons/star.svg";
import ArrowSvg from "/public/images/icons/arrow.svg";
import { useRouter } from "next/router";


import CadenSvg from "/public/images/logos/caden/caden.svg";
import TapfiliateSvg from "/public/images/logos/tapfiliate/tapfiliate.svg";
import HrCloudSvg from "/public/images/logos/hrCloud/hrCloud.svg";
import SmartTechnologiesSvg from "/public/images/logos/smartTechnologies/smartTechnologies.svg";
import ScreensightSvg from "/public/images/logos/screensight/screensight.svg";
import PerformanceAnalyticsSvg from "/public/images/logos/performanceAnalytics/performanceAnalytics.svg";
import GettrxSvg from "/public/images/logos/gettrx/gettrx.svg";
import NativeSvg from "/public/images/logos/native/native.svg";
import DotTechSvg from "/public/images/logos/dotTech/dotTech.svg";
import TymeshiftSvg from "/public/images/logos/tymeshift/tymeshift.svg";
import PressableSvg from "/public/images/logos/pressable/pressable.svg";
import BugfenderSvg from "/public/images/logos/bugfender/bugfender.svg";

import WebbyBadge from "/public/images/badges/webby.png";
import HubspotBadge from "/public/images/badges/hubspot.png";
import GoogleBadge from "/public/images/badges/Google_partner.jpg";
import ClutchBadge from "/public/images/badges/Clutch.png";
import BbbBadge from "/public/images/badges/bbb.webp";
import KlaviyoBadge from "/public/images/badges/klaviyo.png";
import MicrosoftBadge from "/public/images/badges/MicrosoftAds.png";
import LinkedinBadge from "/public/images/badges/LinkedIn.jpg";
import ShortyBadge from "/public/images/badges/Shorty.png";



import Wistia from "@/components/wistia";

const ventures = [
  { src: WebbyBadge, alt: "Webby", key: "webby" },
  { src: HubspotBadge, alt: "HubspotBadge", key: "HubspotBadge" },
  { src: GoogleBadge, alt: "GoogleBadge", key: "GoogleBadge" },
  { src: ClutchBadge, alt: "ClutchBadge", key: "ClutchBadge" },
  { src: BbbBadge, alt: "BbbBadge", key: "BbbBadge" },
  { src: KlaviyoBadge, alt: "KlaviyoBadge", key: "KlaviyoBadge" },
  { src: MicrosoftBadge, alt: "MicrosoftBadge", key: "MicrosoftBadge" },
  { src: LinkedinBadge, alt: "LinkedinBadge", key: "LinkedinBadge" },
  { src: ShortyBadge, alt: "ShortyBadge", key: "ShortyBadge" },
];

const tactics = [
  "Product-led growth",
  "Community-led growth",
  "Account-based marketing (ABM)",
  "Performance marketing funnels",
  "LinkedIn advertising",
  "Google advertising",
  "Meta advertising",
  "Content marketing",
  "Opt-in magnet",
  "Search engine optimization",
  "SEO-focused PR",
  "Podcast advertising",
  "App store optimization",
  "Lead buy management",
  "Email automation",
  "Cold email",
  "Direct mail",
  "Landing page optimization",
  "Microsite development",
  "Conversion tracking",
  "Stunt marketing",
  "Influencer marketing",
  "Ad creative development",
  "Sales collateral creation",
  "Product Hunt launches",
  "Copywriting",
  "Landing page design/development",
  "Custom audience sourcing and scraping",
  "And some other tactics we can't talk about publicly ;)",
];

const testimonials = [
  {
    quote: "I've been working with the Deviate Labs team for over a year now and it has been a fantastic experience! As our project has evolved, the team was able to adapt and optimize for each stage. Deviate Labs is a creative and resourceful team with many facets of expertise — <b>I highly recommend their services.</b>",
    name: "Manat McLeod",
    company: "SMART Technologies",
    image: {
      base: "/images/portraits/manatMcleod/manatMcleod",
      widths: [200],
      exts: ["webp", "png"],
    },
  },
  {
    quote: "We have been working with Deviate Labs since March 2017. It started with an introductory call which lasted for 45 min. <b>I knew that they would be the guys to help us expand</b> our business and get more customers. Deviate Labs has been helping me with content marketing and running Google ads for our product and so far it's productive. I would recommend Deviate Labs for anyone who wants to expand their business and see a rapid growth.",
    name: "Krishna Surendra",
    company: "HR Cloud",
    image: {
      base: "/images/portraits/krishnaSurendra/krishnaSurendra",
      widths: [200],
      exts: ["webp", "png"],
    },
  },
  {
    quote: "After having gone through several marketing agencies, <b>Deviate Labs is the first one to truly 'get' it.</b> Most marketing agencies we worked with had their formula, which they then tried to apply to our business. Deviate Labs really looked at what was needed for our specific business and then came up with a solid combination of tried and tested channels, as well as more novel approaches. We have been hitting record after record ever since. 10/10 recommend.",
    name: "Thomas van der Kleij",
    company: "Tapfiliate",
    image: {
      base: "/images/portraits/thomasVanDerKleij/thomasVanDerKleij",
      widths: [200],
      exts: ["webp", "png"],
    },
  },
];

const caseStudies = [
  {
    title: "Subliminal Messaging Meets Escape Rooms on Black Friday for Virality",
    description: "Radix, the parent company of .TECH which owns several other top-level domains like .STORE, .SPACE, and .SITE, dubbed BreakTheCode.Tech the most successful marketing campaign in the history of their company. Find out why.",
    url: "/resources/growth-hacking-viral-campaign/",
  },
  {
    title: "How We Rocketed to 10,000 Beta Signups on a $350 Budget",
    description: "Caden, a privacy tech company, was just coming out of stealth and wanted to generate 10,000 Beta signups in advance of launching their app on a \"shoestring\" budget. Find out how we surpassed their goal.",
    url: "/resources/how-we-rocketed-to-10-000-beta-signups-on-a-350-budget/",
  },
  //{
  //title: "SEO Super Stardom",
  //description: "Tapfiliate, an affiliate marketing platform for businesses, leveraged SEO to top the search results for a steady flow of highly qualified leads. Find out how.",
  //url: "",
  //},
];

export default function B2bSaasMarketingPage({buttonColor, h1}) {
  const router = useRouter();

  const handleClick = () => {
    exitPopup.style.display = "flex";
  };

  //logo carousel animation
  const logoRefs = useRef([]);
  useEffect(() => {
    if (logoRefs.current){
      let loop = horizontalLoop(logoRefs.current, {paused: false, repeat: -1, speed: 1});
      //reset the animation and destroy it so we can make a new one safely
      return () => {
        loop.progress(0);
        loop.kill();
      };
    }
  }, [logoRefs]);

  useEffect(() => {
    if (router.isReady){
      router.replace({
        query: { ...router.query, color: buttonColor, h1: h1.join(" ") },
      });
    }
  }, [buttonColor, router.isReady]);


  return <GenericLayout
    title="B2B SaaS Growth Marketing | Deviate Labs"
    description="If your SaaS startup is seeking to grab attention with an effective marketing stunt, Deviate Labs can help you attract eyeballs - and customers."
    color="purple"
    canonical="/services/b2b-saas-growth-marketing/"
  >
    <section className={`section ${css.fold}`}>
      <div className="sectionInner">
        <div className={css.logo}><DlLogoSvg /></div>
        <h1>{h1.map((row, index) => <div key={index}>{row}</div>)}</h1>
        <div className={css.subheadline}>Demand Generation | Performance Marketing | Growth Hacking</div>
        <div>
          <Button label="Contact Us" color={buttonColor} className={css.cta} href="/contact" large />
          <Button className={css.secondCta} label="Download Agency Overview" onClick={handleClick} light ghost />
        </div>
        <div className={css.clients}>
          <div className={css.logos}>
            {ventures.map((venture, idx) => (
              <div key={venture.key} className={css.cardWrapper} ref={(el) => (logoRefs.current[idx] = el)}>
                <Card dark className={css.card}>
                  <div className={css.cardInner}>
                    <img src={venture.src.src} alt={venture.alt} />
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className={`section ${css.about}`}>
      <div className="sectionInner">
        <h2>About Deviate Labs</h2>
        <div className={css.row}>
          <div className={css.text}>
            <p>We&apos;re a little biased, but Deviate Labs is the best B2B SaaS growth marketing agency in America. What substantiates our claim is that we&apos;re the agency you hire when you&apos;re looking for that X-factor.</p>
            <p><b>Like every other B2B SaaS growth marketing agency,</b> we build high-performing demand generation funnels fueled by performance marketing best practices to predictably produce marketing qualified leads.</p>
            <p><b>Unlike every other B2B SaaS growth marketing agency,</b> we pair our bespoke services with creative marketing stunts, clever growth hacking tactics (btw we literally wrote the book on <Link href="/book/">Growth Hacking</Link>), and other organic X-factor campaigns in pursuit of the coveted $0 marginal cost customer.</p>
            <p>That&apos;s the Deviate Labs difference.</p>
          </div>
          <Wistia
            videoId="03cjmsuqwh"
            imageBase="/images/videos/sizzleReel/sizzleReel"
          />
        </div>
      </div>
    </section>

    <section className={`section ${css.process}`}>
      <div className="sectionInner">
        <h2>The Deviate Labs Advantage</h2>
        <div className={css.subheadline}>Why B2B SaaS companies choose Deviate Labs...</div>

        <div className={css.cards}>
          <Card dark className={css.card} headline="Moving at Startup Speed">
            <div className={css.cardInner}>Most other B2B SaaS growth marketing agencies you talk to are going to want ~3 months to pontificate and plan before any of the real work gets done. A quarterly cadence is fine for Fortune 500 companies reporting quarterly results, and may have been functional a decade or two ago. However, if you&apos;re going-to-market in a market that changes monthly, your market is materially different by the time you launch and you want an agency that can move at startup speed.</div>
          </Card>
          <Card dark className={css.card} headline="Strategy and Execution in Tandem">
            <div className={css.cardInner}>An example of what the parallelization of strategy and execution might look like is to kick-off a go-to-market planning document complete with competitor deep-dives, customer research, stunt marketing ideation, etc. while simultaneously confirming ad copy, ad design and ad configuration in a programmatic ad platform. Then, rather than relying on keyword CPC guesses, we use a modest budget to get real time in-market data around value propositions, visuals, etc. and benefit from the inbound lead flow and subsequent sales team learnings.</div>
          </Card>
          <Card dark className={css.card} headline="Consumerization of the Enterprise">
            <div className={css.cardInner}>Another common throughline in Deviate Labs&apos; clients is the desire for the &quot;consumerization&quot; of their marketing funnels. Traditionally, B2B marketing focused on corporations rather than individuals using the products or services, but this approach has fallen flat in recent years in favor of directly addressing the purchasers (and users) of these B2B offerings. &quot;Consumerization&quot; of the enterprise manifests in marketing in a number of different manners from persona-matched copywriting to emotion-laden creative to consumer-inspired campaigns designed to develop a groundswell for your solution and break-through B2B committee-based decision making.</div>
          </Card>
        </div>
      </div>
    </section>

    <section className={`section ${css.tactics}`}>
      <div className="sectionInner">
        <h2>B2B SaaS Growth Marketing Tactics</h2>
        <div className={css.subheadline}>We can handle full-funnel demand generation and give you the X-factor you need to drive organic growth.</div>
        <div className={css.cards}>
          {tactics.map((tactic) => <Card key={tactic} dark className={css.card}>
            <div className={css.cardInner}><CircleCheckmarkSvg/>{tactic}</div>
          </Card>)}
        </div>
      </div>
    </section>

    <section className={`section ${css.testimonials}`}>
      <div className="sectionInner">
        <h2>Customer Testimonials</h2>
        <div className={css.cards}>
          {testimonials.map((testimonial) => <Card key={testimonial.name} className={css.card}>
            <div className={css.cardInner}>
              <div className={css.quote} dangerouslySetInnerHTML={{__html: testimonial.quote}}></div>
              <div className={css.stars}><StarSvg/><StarSvg/><StarSvg/><StarSvg/><StarSvg/></div>

              <div className={css.author}>
                <div className={css.picture}>
                  <Picture
                    base={testimonial.image.base}
                    alt="Profile picture"
                    exts={testimonial.image.exts}
                    widths={testimonial.image.widths}
                    loading="lazy"
                    sizes="200px"
                  />
                </div>
                <div className={css.name}>{testimonial.name}</div>
                <div className={css.company}>{testimonial.company}</div>
              </div>
            </div>

          </Card>)}

        </div>
      </div>
    </section>

    <section className={`section ${css.caseStudies}`}>
      <div className="sectionInner">
        <h2>
          <div>See Our B2B SaaS Growth</div>
          <div>Marketing in Action</div>
        </h2>


        <div className={css.cards}>
          {caseStudies.map((caseStudy) => <Card key={caseStudy.title} className={css.card} href={caseStudy.url} target="_blank">
            <div className={css.cardInner}>
              <div className={css.logo}>
                <DeviateLabsSealSvg/>
              </div>
              <div className={css.title}>{caseStudy.title}</div>
              <div className={css.description}>{caseStudy.description}</div>

              <div className={css.cta}>Read More<ArrowSvg/></div>
            </div>
          </Card>)}
        </div>
      </div>
    </section>


    <ContactSection/>
  </GenericLayout>;
}


export function getStaticProps() {
  let colors = ["red", "cyan", "yellow", "green", "pink"];

  let h1s = [
    [
      "The Best Damn B2B SaaS",
      "Growth Marketing Agency",
    ],
    [
      "Innovative B2B SaaS",
      "Growth Marketing Agency",
    ],
  ];

  return {
    props: {
      buttonColor: colors[Math.floor(Math.random() * colors.length)],
      h1: h1s[Math.floor(Math.random() * h1s.length)],
    },
    revalidate: 60,
  };
}
